/**
 * Interactive HTML snapshot serializer (S15, Pro) — PURE, no React/store/IPC.
 * Produces one self-contained HTML document string: baked tile data (row
 * cap `HTML_SNAPSHOT_ROW_CAP`), a read-only parameter/filter header strip,
 * hover tooltips on chart bars (native SVG `<title>`), local client-side
 * sort on table tiles, and a light/dark theme toggle — both themes are
 * always baked (`SNAPSHOT_STYLE_CSS` defines both; the toggle button flips
 * a `dark` class client-side). NO re-query, NO cross-filtering: every value
 * on the page was resolved at export time.
 *
 * SECURITY — every value that reaches the page goes through `escapeHtml`;
 * the inline `<style>`/`<script>` are fixed constants allow-listed by SHA-256
 * hash in the CSP meta tag (`default-src 'none'`), so nothing else can run
 * or load, including anything baked into a cell/title/markdown value. The
 * caller decides what data to pass in — this module never reaches for a
 * connection id, connection name, or database name, and only ever reads a
 * tile's `.source.sql` when `includeQueries` is true.
 */
import type { QueryResultSet } from '../../../../types/queryExecution';
import type { DashboardTile } from '../types/dashboardTypes';
import { isQueryBackedTile } from '../types/dashboardTypes';
import {
  escapeHtml,
  SNAPSHOT_SCRIPT_HASH,
  SNAPSHOT_SCRIPT_JS,
  SNAPSHOT_STYLE_CSS,
  SNAPSHOT_STYLE_HASH,
} from './htmlSnapshotAssets';
import { buildNoteTileHtml, buildQueryTileBodyHtml, tileSourceSql } from './htmlSnapshotTiles';

export type HtmlSnapshotParam = { label: string; display: string };
export type HtmlSnapshotFilter = { field: string; display: string };

export type BuildHtmlSnapshotInput = {
  dashboardName: string;
  /** Optional user-authored dashboard description, rendered under the title. */
  dashboardDescription?: string;
  /** ISO timestamp — rendered verbatim (escaped) in the header. */
  exportedAt: string;
  /** Theme baked as the DEFAULT; the toggle button switches client-side. */
  theme: 'light' | 'dark';
  /** When true, each query-backed tile's SQL is shown (never connection/database). */
  includeQueries: boolean;
  params: HtmlSnapshotParam[];
  filters: HtmlSnapshotFilter[];
  tiles: DashboardTile[];
  /** Keyed by tile id — only tiles with a successful run get an entry. */
  resultsByTileId: Record<string, QueryResultSet | undefined>;
};

function renderTileShell(tile: DashboardTile, bodyHtml: string): string {
  const { x, y, w, h } = tile.layout;
  return `<section class="snap-tile" style="grid-column:${x + 1} / span ${w};grid-row:${y + 1} / span ${h}" data-tile-id="${escapeHtml(tile.id)}">
      <h2>${escapeHtml(tile.title)}</h2>
      <div class="snap-tile-body">${bodyHtml}</div>
    </section>`;
}

function renderTile(tile: DashboardTile, input: BuildHtmlSnapshotInput): string {
  if (!isQueryBackedTile(tile)) {
    return renderTileShell(tile, buildNoteTileHtml(tile));
  }
  const result = input.resultsByTileId[tile.id];
  if (!result) {
    return renderTileShell(tile, '<div class="snap-empty">No data — this tile hasn’t run successfully</div>');
  }
  let body = buildQueryTileBodyHtml(tile, result);
  if (input.includeQueries) {
    body += `<details class="snap-sql"><summary>SQL</summary><pre>${escapeHtml(tileSourceSql(tile))}</pre></details>`;
  }
  return renderTileShell(tile, body);
}

function renderHeader(input: BuildHtmlSnapshotInput): string {
  const paramChips = input.params
    .map((p) => `<span class="snap-chip">${escapeHtml(p.label)}: ${escapeHtml(p.display)}</span>`)
    .join('');
  const filterChips = input.filters
    .map((f) => `<span class="snap-chip">Filter: ${escapeHtml(f.field)} = ${escapeHtml(f.display)}</span>`)
    .join('');
  const chips = paramChips || filterChips ? `<div class="snap-chips">${paramChips}${filterChips}</div>` : '';
  return `<header class="snap-header" data-testid="snapshot-header">
    <div>
      <h1>${escapeHtml(input.dashboardName)}</h1>
      ${input.dashboardDescription?.trim() ? `<div class="snap-meta">${escapeHtml(input.dashboardDescription.trim())}</div>` : ''}
      <div class="snap-meta">Exported ${escapeHtml(input.exportedAt)} — interactive snapshot, data baked at export time (not live)</div>
      ${chips}
    </div>
    <button id="theme-toggle-btn" type="button" data-testid="snapshot-theme-toggle">Toggle theme</button>
  </header>`;
}

/** Builds the full self-contained HTML document string for one dashboard. */
export function buildHtmlSnapshot(input: BuildHtmlSnapshotInput): string {
  const ordered = [...input.tiles].sort(
    (a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x,
  );
  const tilesHtml = ordered.map((tile) => renderTile(tile, input)).join('\n');
  const csp = `default-src 'none'; style-src 'sha256-${SNAPSHOT_STYLE_HASH}'; script-src 'sha256-${SNAPSHOT_SCRIPT_HASH}'`;

  return `<!DOCTYPE html>
<html${input.theme === 'dark' ? ' class="dark"' : ''}>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta http-equiv="Content-Security-Policy" content="${csp}">
<title>${escapeHtml(input.dashboardName)} — Dashboard Snapshot</title>
<style>${SNAPSHOT_STYLE_CSS}</style>
</head>
<body>
${renderHeader(input)}
<main class="snap-grid" data-testid="snapshot-grid">
${tilesHtml}
</main>
<footer class="snap-footer">Exported from Jam SQL Studio — this is a point-in-time snapshot and does not refresh.</footer>
<script>${SNAPSHOT_SCRIPT_JS}</script>
</body>
</html>
`;
}
